import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { EObservableElementProp, IObservableElementProps } from '../../directives';
import { ComponentPrototype, isTrue } from '../../shared';

const STATIC_CONTAINER = 'scrollable-panel__static';

@Component({
  selector: 'app-scrollable-panel',
  template: `
    <div class="scrollable-panel" [class.showButtons]="showButtons$ | async">
      <div
        class="scrollable-panel__static"
        appObservableElement="width"
        [observable$]="static$"
        [class.smoothScroll]="smoothScroll$ | async"
      >
        <div class="scrollable-panel__content" appObservableElement="width" [observable$]="scrollable$">
          <ng-content></ng-content>
        </div>
      </div>
      <ng-container *ngIf="showButtons$ | async">
        <div
          class="scrollable-panel__button left-button"
          (mousedown)="delta$.value !== -1 && delta$.next(-1)"
          (mouseup)="delta$.next(null)"
          (mouseleave)="delta$.next(null)"
        >
          <i class="fa fa-chevron-left"></i>
        </div>
        <div
          class="scrollable-panel__button right-button"
          (mousedown)="delta$.value !== 1 && delta$.next(1)"
          (mouseup)="delta$.next(null)"
          (mouseleave)="delta$.next(null)"
        >
          <i class="fa fa-chevron-right"></i>
        </div>
      </ng-container>
    </div>
  `,
  styleUrls: ['./scrollable-panel.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ScrollablePanelComponent extends ComponentPrototype implements OnInit {
  @Input() public scrollStep: number = 100;

  @Output() public componentReady: EventEmitter<ScrollablePanelComponent> = new EventEmitter<
    ScrollablePanelComponent
  >();

  private staticContainer: HTMLElement;

  public static$: BehaviorSubject<IObservableElementProps> = new BehaviorSubject<IObservableElementProps>(null);

  public scrollable$: BehaviorSubject<IObservableElementProps> = new BehaviorSubject<IObservableElementProps>(null);

  public readonly showButtons$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public readonly smoothScroll$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public readonly delta$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  public scrollToStep = (delta: number): this => {
    this.staticContainer.scrollLeft = this.staticContainer.scrollLeft + this.scrollStep * delta;
    return this;
  };

  public scrollToElement = (elem: HTMLElement): this => {
    const containerWidth = this.static$.value?.[EObservableElementProp.WIDTH] || 0;
    const elemWidth = elem.getBoundingClientRect()?.[EObservableElementProp.WIDTH] || 0;
    const offset = (containerWidth - elemWidth) / 2;

    this.staticContainer.scrollLeft = elem.offsetLeft - (this.staticContainer.offsetLeft + offset);
    return this;
  };

  constructor(private ref: ElementRef) {
    super();
  }

  ngOnInit(): void {
    const { showButtons$, scrollable$, static$ } = this;

    this.staticContainer = this.ref.nativeElement.querySelector(`.${STATIC_CONTAINER}`);

    const runSmoothScrollFrame = (): void => {
      const delta = this.delta$.value;
      delta && this.scrollToStep(delta) && setTimeout(() => runSmoothScrollFrame(), 150);
    };

    this.registerSubscriptions(
      combineLatest([scrollable$, static$])
        .pipe(
          filter(values => values.every(isTrue)),
          map(([scrollableElem, staticElem]) => scrollableElem.width > staticElem.width),
          tap(showButtons => showButtons$.next(showButtons))
        )
        .subscribe(),

      this.delta$.pipe(tap(delta => delta && runSmoothScrollFrame())).subscribe()
    );

    setTimeout(() => this.componentReady.emit(this));
  }
}
